import {
  collection,
  documentId,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query as fsQuery,
  startAfter,
  Timestamp,
  where,
  type DocumentData,
  type Query,
  type QueryConstraint,
} from 'firebase/firestore';
import { getDb } from '../../auth/firebase';
import type { WatchEvent, Unsubscribe, DocChange } from '../collection';
import type { LedgerQuery } from './types';
import type {
  ClientKey,
  Deployment,
  Estate,
  RouterTokenRecord,
  TenantSummary,
  UsageRow,
} from '../../data/types';
import { mapClientKey, mapDeployment, mapEstate, mapTenant, mapToken, mapUsage } from './firestoreMappers';

/** Lane A: the live rows.
 *
 *  Every table in the console is a Firestore collection watch, because a table
 *  that can only poll cannot show a conflict arriving. The security rules admit
 *  a superadmin to exactly these collections and to nothing that carries key
 *  material — a rule grants a whole document, so the pool and BYO-key
 *  collections are structurally closed to a browser and go over HTTP instead. */

/** watch adapts an onSnapshot stream to the store's event shape.
 *
 *  The FIRST snapshot is a `reset`, not a set of `added` changes: a delta
 *  stream alone cannot say "this query matched nothing", so the initial result
 *  has to be its own kind of event or an empty collection is indistinguishable
 *  from one that never loaded. */
function watch<T>(
  build: () => Query<DocumentData>,
  map: (id: string, data: DocumentData) => T,
  on: (ev: WatchEvent<T>) => void,
  cursorOf?: (docs: { id: string; data: DocumentData }[]) => string | undefined,
): Unsubscribe {
  let first = true;
  // Query construction is synchronous and CAN throw — a bad cursor, an
  // unsupported filter combination. Left uncaught it escapes the store's
  // subscribe call and blanks the whole console, so it becomes an error event
  // like any other: one table reports a problem, the rest keep working.
  let q: Query<DocumentData>;
  try {
    q = build();
  } catch (err) {
    on({ type: 'error', message: err instanceof Error ? err.message : String(err) });
    return () => {};
  }
  return onSnapshot(
    q,
    (snap) => {
      if (first) {
        first = false;
        const docs = snap.docs.map((d) => ({ id: d.id, doc: map(d.id, d.data()) }));
        on({
          type: 'reset',
          docs,
          nextCursor: cursorOf?.(snap.docs.map((d) => ({ id: d.id, data: d.data() }))),
        });
        return;
      }
      // Recompute the page boundary on EVERY snapshot. The window is bounded
      // and moves as rows arrive, so the cursor captured at first load goes
      // stale — and because a change event carries no cursor at all, the first
      // live completion would otherwise blank it and disable "Next".
      const boundary = cursorOf?.(snap.docs.map((d) => ({ id: d.id, data: d.data() })));
      const changes: DocChange<T>[] = snap.docChanges().map((c) =>
        c.type === 'removed'
          ? { kind: 'removed', id: c.doc.id }
          : {
              kind: c.type === 'added' ? 'added' : 'modified',
              id: c.doc.id,
              doc: map(c.doc.id, c.doc.data()),
            },
      );
      // A snapshot with no document changes is a metadata-only event. Emitting
      // it would be a no-op the store has to filter anyway.
      if (changes.length > 0) on({ type: 'change', changes, nextCursor: boundary });
    },
    (err) => {
      // An error KEEPS the rows on screen — the store only moves phase. A
      // transient rules or network failure must not blank an operator's table.
      on({ type: 'error', message: err.message });
    },
  );
}

const db = () => {
  const d = getDb();
  if (!d) throw new Error('Firestore is not configured in this build');
  return d;
};

/** encodeCursor / decodeCursor keep paging opaque to the console.
 *
 *  The store only ever replays a cursor the source handed it, so the format is
 *  private here. It carries the sort key AND the document id because two rows
 *  can share an instant, and startAfter on the value alone would skip or repeat
 *  one of them. */
function encodeCursor(occurredAt: unknown, id: string): string {
  // Seconds and NANOSECONDS, not an ISO string. A Firestore Timestamp carries
  // nanosecond precision and Date truncates to milliseconds, so a round trip
  // through ISO moves the boundary earlier — and in a descending order that
  // silently SKIPS every row between the true boundary and the truncated one.
  const t = occurredAt instanceof Timestamp ? occurredAt : null;
  if (!t) return `0.0|${id}`;
  return `${t.seconds}.${t.nanoseconds}|${id}`;
}

/** decodeCursor is encodeCursor's inverse, preserving nanoseconds. */
function decodeCursor(cursor: string): { at: Timestamp; id: string } | null {
  const [stamp, ...rest] = cursor.split('|');
  const id = rest.join('|');
  const [secs, nanos] = stamp.split('.');
  const s = Number(secs);
  const n = Number(nanos);
  if (!Number.isFinite(s) || !Number.isFinite(n) || !id) return null;
  return { at: new Timestamp(s, n), id };
}

export function createFirestoreLive() {
  return {
    kind: 'live' as const,

    watchEstates(on: (ev: WatchEvent<Estate>) => void): Unsubscribe {
      return watch(
        () => fsQuery(collection(db(), 'router_estates')), mapEstate, on);
    },

    watchDeployments(
      estateId: string | undefined,
      on: (ev: WatchEvent<Deployment>) => void,
    ): Unsubscribe {
      const cs: QueryConstraint[] = estateId ? [where('estateId', '==', estateId)] : [];
      return watch(
        () => fsQuery(collection(db(), 'router_deployments'), ...cs, fsLimit(500)), mapDeployment, on);
    },

    watchTokens(
      estateId: string | undefined,
      on: (ev: WatchEvent<RouterTokenRecord>) => void,
    ): Unsubscribe {
      const cs: QueryConstraint[] = estateId ? [where('estateId', '==', estateId)] : [];
      return watch(
        () => fsQuery(collection(db(), 'router_tokens'), ...cs, orderBy('issuedAt', 'desc'), fsLimit(500)),
        mapToken,
        on,
      );
    },

    watchClientKeys(
      estateId: string | undefined,
      on: (ev: WatchEvent<ClientKey>) => void,
    ): Unsubscribe {
      const cs: QueryConstraint[] = estateId ? [where('estateId', '==', estateId)] : [];
      return watch(
        () => fsQuery(collection(db(), 'router_keys'), ...cs, fsLimit(500)), mapClientKey, on);
    },

    watchTenants(
      estateId: string | undefined,
      on: (ev: WatchEvent<TenantSummary>) => void,
    ): Unsubscribe {
      const cs: QueryConstraint[] = estateId ? [where('estateId', '==', estateId)] : [];
      return watch(
        () => fsQuery(collection(db(), 'router_tenants'), ...cs, fsLimit(1000)),
        mapTenant,
        on,
      );
    },

    watchLedger(q: LedgerQuery, on: (ev: WatchEvent<UsageRow>) => void): Unsubscribe {
      const cs: QueryConstraint[] = [];
      if (q.estateId) cs.push(where('estateId', '==', q.estateId));
      if (q.tenantId) cs.push(where('tenantId', '==', q.tenantId));

      // The filters Firestore can actually serve. `conflicts` is deliberately
      // absent: conflictOf is omitempty, so it is missing on almost every
      // document, and Firestore has no "field exists" predicate that does not
      // force the ordering onto that field — which would sort the incident view
      // by digest instead of by time. It narrows client-side instead.
      if (q.filter === 'billable') cs.push(where('billable', '==', true));
      if (q.filter === 'attributed') cs.push(where('billable', '==', false));
      if (q.filter === 'errors') {
        cs.push(
          where('status', 'in', [
            'provider_error', 'canceled', 'invalid_request',
            'provider_unavailable', 'rate_limited', 'internal',
          ]),
        );
      }
      // Search is an EXACT event id or request id — the operator's actual use.
      // Firestore does no substring or case-insensitive matching, and a search
      // box that silently ignored three of its four fields would be a search
      // box that lies.
      const term = q.search?.trim();
      if (term) cs.push(where('eventId', '==', term));

      cs.push(orderBy('occurredAt', 'desc'));
      // The document id is ordered EXPLICITLY. Firestore appends __name__ for
      // uniqueness on its own, but it does not count toward startAfter's arity
      // — so a cursor carrying both values needs both clauses named, or the
      // SDK rejects it with "too many arguments".
      //
      // It is also what makes paging correct rather than merely working: two
      // completions can share an instant, and a cursor on the timestamp alone
      // would skip or repeat one of them.
      cs.push(orderBy(documentId(), 'desc'));
      const cur = q.cursor ? decodeCursor(q.cursor) : null;
      if (cur) cs.push(startAfter(cur.at, cur.id));
      cs.push(fsLimit(q.limit));

      return watch(
        () => fsQuery(collection(db(), 'router_usage'), ...cs),
        mapUsage,
        on,
        (docs) => {
          // Only offer a next cursor when the page came back full: a short page
          // is the end, and offering one anyway makes "Next" lead nowhere.
          if (docs.length < q.limit) return undefined;
          const last = docs[docs.length - 1];
          return encodeCursor(last.data.occurredAt, last.id);
        },
      );
    },
  };
}
